/**
 * Sanitizar Dados — as tarefas de limpeza da base, cada uma a um clique.
 *
 * Antes isso morava em `scripts/`, rodado à mão no console de produção. A lógica é a MESMA
 * (`bhunter_core.cadastro.saneamento`); aqui só há o disparo e o acompanhamento.
 *
 * O desenho segue uma regra: **ver antes de apagar**. Toda tarefa roda primeiro em modo RELATÓRIO,
 * que conta o estrago e não escreve nada — o botão de aplicar só aparece depois que o relatório
 * mostrou o que existe, e nas tarefas destrutivas ainda pede confirmação. Uma limpeza que apaga
 * milhares de linhas não pode ser um clique distraído.
 */
import { AlertTriangle, Brush, Check, Play, RefreshCw, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Skeleton, Spinner } from '@/components/ui/misc';
import {
  estadoSaneamento,
  executarSaneamento,
  listarTarefasSaneamento,
  type TarefaSaneamento,
} from '@/lib/api';

const EM_CURSO = new Set(['na_fila', 'executando']);

const ROTULO_ESTADO: Record<string, string> = {
  na_fila: 'Na fila',
  executando: 'Executando',
  concluida: 'Concluída',
  erro: 'Erro',
};

function corEstado(estado: string): string {
  if (estado === 'concluida') return 'border-success/40 text-success';
  if (estado === 'erro') return 'border-danger/40 text-danger';
  if (EM_CURSO.has(estado)) return 'border-warning/40 text-warning';
  return 'text-text-tertiary';
}

function quando(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export default function Sanitizar() {
  const [tarefas, setTarefas] = useState<TarefaSaneamento[] | null>(null);
  const [ocupada, setOcupada] = useState<string | null>(null);
  // Tarefa cuja destruição está pendente de confirmação (o segundo clique).
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const vivo = useRef(true);

  const carregar = useCallback(async () => {
    const r = await listarTarefasSaneamento();
    if (vivo.current && r.ok) setTarefas(r.dados);
  }, []);

  useEffect(() => {
    vivo.current = true;
    void carregar();
    return () => {
      vivo.current = false;
    };
  }, [carregar]);

  // Enquanto alguma tarefa estiver rodando, recarrega sozinho — o operador não fica no escuro.
  useEffect(() => {
    const rodando = tarefas?.some((t) => t.ultima && EM_CURSO.has(t.ultima.estado));
    if (!rodando) return;
    const id = setTimeout(() => void carregar(), 2000);
    return () => clearTimeout(id);
  }, [tarefas, carregar]);

  async function disparar(t: TarefaSaneamento, modo: 'relatorio' | 'aplicar') {
    setOcupada(t.chave);
    setConfirmando(null);
    const r = await executarSaneamento(t.chave, modo);
    setOcupada(null);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    toast.success(modo === 'relatorio' ? 'Verificando…' : 'Aplicando…');
    await carregar();
    // Acompanha até terminar, para o resultado aparecer sem o operador ter de recarregar.
    void acompanhar(r.dados.id);
  }

  async function acompanhar(id: string) {
    for (let i = 0; i < 300 && vivo.current; i++) {
      await new Promise((s) => setTimeout(s, 2000));
      const r = await estadoSaneamento(id);
      if (!r.ok) return;
      if (!EM_CURSO.has(r.dados.estado)) {
        await carregar();
        if (r.dados.estado === 'erro') toast.error(r.dados.mensagem ?? 'Falhou.');
        else if (r.dados.alteradas) toast.success(`${r.dados.alteradas} linha(s) alterada(s).`);
        return;
      }
      await carregar();
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 tipo-titulo">
          <Brush className="size-5 text-accent" />
          Sanitizar dados
        </h1>
        <Button variant="secundario" onClick={() => void carregar()}>
          <RefreshCw className="size-4" />
          Atualizar
        </Button>
      </div>
      <p className="max-w-3xl tipo-corpo-pequeno text-text-tertiary">
        Cada tarefa roda primeiro em <strong className="text-text-secondary">Verificar</strong>, que
        conta o que existe e <strong className="text-text-secondary">não escreve nada</strong>. Só
        depois de ver o resultado o botão de aplicar fica disponível.
      </p>

      {tarefas === null ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        tarefas.map((t) => (
          <CartaoTarefa
            key={t.chave}
            tarefa={t}
            ocupada={ocupada === t.chave}
            confirmando={confirmando === t.chave}
            aoVerificar={() => void disparar(t, 'relatorio')}
            aoAplicar={() => {
              if (t.destrutiva && confirmando !== t.chave) {
                setConfirmando(t.chave);
                return;
              }
              void disparar(t, 'aplicar');
            }}
            aoCancelar={() => setConfirmando(null)}
          />
        ))
      )}
    </div>
  );
}

function CartaoTarefa({
  tarefa,
  ocupada,
  confirmando,
  aoVerificar,
  aoAplicar,
  aoCancelar,
}: {
  tarefa: TarefaSaneamento;
  ocupada: boolean;
  confirmando: boolean;
  aoVerificar: () => void;
  aoAplicar: () => void;
  aoCancelar: () => void;
}) {
  const u = tarefa.ultima;
  const rodando = !!u && EM_CURSO.has(u.estado);
  // Só se aplica o que já foi verificado: sem um relatório recente, o operador estaria apagando
  // às cegas. Um "aplicar" anterior também libera (ele já viu o que havia).
  const verificado = !!u && u.estado === 'concluida';
  const temPendencia = verificado && Object.keys(u.itens).length > 0 && u.mensagem !== null;
  const limpo = verificado && u.modo === 'relatorio' && Object.keys(u.itens).length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <CardTitle className="flex items-center gap-2">
            {tarefa.titulo}
            {tarefa.destrutiva && (
              <span title="Aplicar apaga linhas">
                <TriangleAlert className="size-4 text-warning" />
              </span>
            )}
          </CardTitle>
          <p className="max-w-2xl tipo-corpo-pequeno text-text-tertiary">{tarefa.descricao}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {u && (
            <Chip className={corEstado(u.estado)}>
              {ROTULO_ESTADO[u.estado] ?? u.estado}
              {u.modo === 'aplicar' && u.estado === 'concluida' ? ' · aplicado' : ''}
            </Chip>
          )}
          <Button variant="secundario" onClick={aoVerificar} disabled={ocupada || rodando}>
            {ocupada || rodando ? <Spinner /> : <Play className="size-4" />}
            Verificar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {rodando && <p className="tipo-corpo-pequeno text-text-tertiary">Executando…</p>}

        {verificado && (
          <>
            <p className="tipo-corpo text-text">{u.mensagem}</p>
            {Object.keys(u.itens).length > 0 && (
              <ul className="flex flex-col gap-1">
                {Object.entries(u.itens).map(([rotulo, n]) => (
                  <li key={rotulo} className="flex gap-2 tipo-corpo-pequeno">
                    <span className="min-w-16 text-right font-medium text-text tabular-nums">
                      {n.toLocaleString('pt-BR')}
                    </span>
                    <span className="text-text-tertiary">{rotulo}</span>
                  </li>
                ))}
              </ul>
            )}
            {u.amostra.length > 0 && (
              <div className="rounded-md border border-border bg-surface-2 p-2">
                <p className="mb-1 tipo-corpo-pequeno text-text-tertiary">Exemplos:</p>
                {u.amostra.map((a) => (
                  <p key={a} className="font-mono text-[12px] text-text-secondary">
                    {a}
                  </p>
                ))}
              </div>
            )}
            <p className="tipo-corpo-pequeno text-text-tertiary">
              {quando(u.quando)}
              {u.modo === 'relatorio' ? ' · nada foi escrito' : ` · ${u.alteradas} alteradas`}
            </p>
          </>
        )}

        {limpo && (
          <p className="flex items-center gap-1.5 tipo-corpo-pequeno text-success">
            <Check className="size-4" />
            Nada a limpar.
          </p>
        )}

        {temPendencia && !rodando && !limpo && (
          <div className="flex flex-col gap-2 border-border border-t pt-3">
            <p className="flex items-start gap-1.5 tipo-corpo-pequeno text-text-secondary">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
              {tarefa.efeito}
            </p>
            {confirmando ? (
              <div className="flex items-center gap-2">
                <span className="tipo-corpo-pequeno text-danger">
                  Isto não tem desfazer. Confirma?
                </span>
                <Button variant="perigo" onClick={aoAplicar} disabled={ocupada}>
                  Sim, aplicar
                </Button>
                <Button variant="secundario" onClick={aoCancelar}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  variant={tarefa.destrutiva ? 'perigo' : 'primario'}
                  onClick={aoAplicar}
                  disabled={ocupada}
                >
                  Aplicar
                </Button>
              </div>
            )}
          </div>
        )}

        {!u && (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Nunca executada. Clique em «Verificar» para saber o que há.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
